import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { saveLayoutTemplate, pubblicaLayout, saveServizi } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Button, Spinner } from '../components/ui';

const PRESET_SERVIZI = [
  { label: 'Ombrellone', haFila: true, colore: '#1f93e0', icona: '☂️' },
  { label: 'Lettino', haFila: false, colore: '#2A6E7F', icona: '🛏' },
  { label: 'Cabina', haFila: false, colore: '#3a8fa3', icona: '🏠' },
  { label: 'Gazebo', haFila: true, colore: '#0f6e56', icona: '⛺' },
  { label: 'Sdraio', haFila: false, colore: '#854F0B', icona: '🪑' },
];

const COLORI = ['#1f93e0', '#2A6E7F', '#3a8fa3', '#0f6e56', '#854F0B', '#7F77DD', '#D85A30', '#D4537E', '#639922', '#BA7517'];

function genId(col, row) {
  return `${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[row] || row}${col + 1}`;
}

function emptyCell(col, row) {
  return { col, row, type: 'vuoto', id: genId(col, row) };
}

function buildGrid(cols, rows, template = []) {
  const map = {};
  template.forEach(c => { map[`${c.col}_${c.row}`] = c; });
  const cells = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      cells.push(map[`${c}_${r}`] || emptyCell(c, r));
  return cells;
}

export default function BeachBuilder() {
  const { beach, admin, refreshBeach } = useAuth();
  const navigate = useNavigate();
  const servizi = beach?.servizi || [];

  const [showServiziPanel, setShowServiziPanel] = useState(false);
  const [localServizi, setLocalServizi] = useState(servizi);
  const [savingServizi, setSavingServizi] = useState(false);
  const [nuovoForm, setNuovoForm] = useState({ label: '', haFila: false, colore: COLORI[0], icona: '🏖️' });

  const [gridCols, setGridCols] = useState(beach?.gridCols || 10);
  const [gridRows, setGridRows] = useState(beach?.gridRows || 5);
  const [colInput, setColInput] = useState(beach?.gridCols || 10);
  const [rowInput, setRowInput] = useState(beach?.gridRows || 5);

  const [selected, setSelected] = useState(servizi[0]?.id || 'vuoto');
  const [saving, setSaving] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [publishDal, setPublishDal] = useState('');
  const [publishAl, setPublishAl] = useState('');
  const [publishing, setPublishing] = useState(false);

  const [celle, setCelle] = useState(() =>
    buildGrid(beach?.gridCols || 10, beach?.gridRows || 5, beach?.layoutTemplate)
  );

  // ─── Drag-paint ────────────────────────────────────────────────────────────
  const isPainting = useRef(false);

  const paintCell = useCallback((col, row) => {
    setCelle(prev => prev.map(c => {
      if (c.col !== col || c.row !== row) return c;
      if (selected === 'vuoto') return emptyCell(col, row);
      const s = localServizi.find(s => s.id === selected);
      return { col, row, type: selected, id: genId(col, row), servizioLabel: s?.label, icona: s?.icona, colore: s?.colore };
    }));
  }, [selected, localServizi]);

  const handleMouseDown = (col, row) => { isPainting.current = true; paintCell(col, row); };
  const handleMouseEnter = (col, row) => { if (isPainting.current) paintCell(col, row); };
  const handleMouseUp = () => { isPainting.current = false; };

  // ─── Resize ────────────────────────────────────────────────────────────────
  const applyResize = (newCols, newRows) => {
    const cols = Math.max(3, Math.min(25, newCols));
    const rows = Math.max(1, Math.min(20, newRows));
    setGridCols(cols); setGridRows(rows);
    setColInput(cols); setRowInput(rows);
    setCelle(prev => buildGrid(cols, rows, prev));
  };

  // ─── Fill row / col ────────────────────────────────────────────────────────
  const fillRow = (rowIdx) => {
    setCelle(prev => prev.map(c => {
      if (c.row !== rowIdx) return c;
      if (selected === 'vuoto') return emptyCell(c.col, rowIdx);
      const s = localServizi.find(s => s.id === selected);
      return { col: c.col, row: rowIdx, type: selected, id: genId(c.col, rowIdx), servizioLabel: s?.label, icona: s?.icona, colore: s?.colore };
    }));
  };

  const fillCol = (colIdx) => {
    setCelle(prev => prev.map(c => {
      if (c.col !== colIdx) return c;
      if (selected === 'vuoto') return emptyCell(colIdx, c.row);
      const s = localServizi.find(s => s.id === selected);
      return { col: colIdx, row: c.row, type: selected, id: genId(colIdx, c.row), servizioLabel: s?.label, icona: s?.icona, colore: s?.colore };
    }));
  };

  const clearAll = () => setCelle(buildGrid(gridCols, gridRows));

  // ─── Gestione servizi inline ───────────────────────────────────────────────
  const addPreset = (preset) => {
    if (localServizi.find(s => s.label === preset.label)) return;
    setLocalServizi(prev => [...prev, { id: `s${Date.now()}`, ...preset }]);
  };

  const addCustomServizio = () => {
    if (!nuovoForm.label.trim()) return;
    setLocalServizi(prev => [...prev, { id: `s${Date.now()}`, ...nuovoForm }]);
    setNuovoForm({ label: '', haFila: false, colore: COLORI[0], icona: '🏖️' });
  };

  const removeServizio = (id) => {
    setLocalServizi(prev => prev.filter(s => s.id !== id));
    setCelle(prev => prev.map(c => c.type === id ? emptyCell(c.col, c.row) : c));
    if (selected === id) setSelected('vuoto');
  };

  const toggleFila = (id) => {
    setLocalServizi(prev => prev.map(s => s.id === id ? { ...s, haFila: !s.haFila } : s));
  };

  const saveServiziHandler = async () => {
    setSavingServizi(true);
    try {
      await saveServizi(admin.beachCode, localServizi);
      await refreshBeach();
      setShowServiziPanel(false);
    } finally { setSavingServizi(false); }
  };

  // ─── Save / Publish ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveLayoutTemplate(admin.beachCode, { gridCols, gridRows, celle });
      await refreshBeach();
    } finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!publishDal || !publishAl) return;
    setPublishing(true);
    try {
      await saveLayoutTemplate(admin.beachCode, { gridCols, gridRows, celle });
      await pubblicaLayout(admin.beachCode, { celle, dal: publishDal, al: publishAl });
      await refreshBeach();
      setShowPublish(false);
      navigate('/beach');
    } finally { setPublishing(false); }
  };

  const counts = localServizi.reduce((acc, s) => {
    acc[s.id] = celle.filter(c => c.type === s.id).length;
    return acc;
  }, {});

  const CELL_SIZE = Math.max(30, Math.min(52, Math.floor(740 / gridCols)));

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-sm)', color: '#fff', textAlign: 'center',
    padding: '5px 6px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '56px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>

      <Topbar
        title="Beach Builder"
        subtitle={`${beach?.nome} · ${gridCols}×${gridRows}`}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" onClick={clearAll}>Pulisci tutto</Button>
            <Button variant="ghost" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size={14} /> : 'Salva bozza'}
            </Button>
            <Button variant="sand" onClick={() => setShowPublish(true)}>Pubblica →</Button>
          </div>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

        {/* ── Toolbar orizzontale ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '10px 20px', background: 'var(--navy-mid)',
          borderBottom: '1px solid var(--border)', flexShrink: 0,
          flexWrap: 'wrap',
        }}>

          {/* Dimensioni */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Griglia</span>
            <input type="number" min="3" max="25" value={colInput}
              onChange={e => setColInput(e.target.value)}
              onBlur={e => applyResize(Number(e.target.value), gridRows)}
              onKeyDown={e => e.key === 'Enter' && applyResize(Number(e.target.value), gridRows)}
              style={inputStyle} />
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>×</span>
            <input type="number" min="1" max="20" value={rowInput}
              onChange={e => setRowInput(e.target.value)}
              onBlur={e => applyResize(gridCols, Number(e.target.value))}
              onKeyDown={e => e.key === 'Enter' && applyResize(gridCols, Number(e.target.value))}
              style={inputStyle} />
          </div>

          <div style={{ width: '1px', height: '28px', background: 'var(--border)' }} />

          {/* Vuoto/Cancella */}
          <ToolbarItem label="Cancella" icona="⬜" colore="#555"
            active={selected === 'vuoto'} onClick={() => setSelected('vuoto')} />

          <div style={{ width: '1px', height: '28px', background: 'var(--border)' }} />

          {/* Servizi */}
          {localServizi.map(s => (
            <ToolbarItem key={s.id} label={s.label} icona={s.icona} colore={s.colore}
              active={selected === s.id} count={counts[s.id] || 0}
              onClick={() => setSelected(s.id)} />
          ))}

          <div style={{ width: '1px', height: '28px', background: 'var(--border)' }} />

          {/* Gestisci servizi */}
          <button
            onClick={() => { setLocalServizi(beach?.servizi || []); setShowServiziPanel(!showServiziPanel); }}
            style={{
              padding: '5px 12px', borderRadius: 'var(--radius-md)', fontSize: '12px',
              border: `1px solid ${showServiziPanel ? 'var(--ocean)' : 'var(--border-mid)'}`,
              background: showServiziPanel ? 'var(--ocean-dim)' : 'transparent',
              color: showServiziPanel ? 'var(--ocean)' : 'var(--text-soft)',
              cursor: 'pointer', fontFamily: 'var(--font-sans)',
            }}>
            + Gestisci servizi
          </button>

          {/* Hint */}
          <div style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--text-muted)' }}>
            💡 <strong style={{ color: 'var(--text-soft)' }}>Drag</strong> per più celle ·
            clicca <strong style={{ color: 'var(--text-soft)' }}>A·B</strong> per tutta la riga ·
            clicca <strong style={{ color: 'var(--text-soft)' }}>1·2</strong> per tutta la colonna
          </div>
        </div>

        {/* ── Pannello gestione servizi (dropdown sotto toolbar) ── */}
        {showServiziPanel && (
          <div style={{
            background: 'var(--navy-mid)', borderBottom: '1px solid var(--border-mid)',
            padding: '16px 20px', display: 'flex', gap: '32px', flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            {/* Servizi attivi */}
            <div style={{ minWidth: '240px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Configurati
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {localServizi.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', padding: '6px 10px',
                  }}>
                    <span style={{ fontSize: '14px' }}>{s.icona}</span>
                    <div style={{ flex: 1, fontSize: '12.5px', fontWeight: 500 }}>{s.label}</div>
                    <button onClick={() => toggleFila(s.id)} style={{
                      padding: '2px 7px', borderRadius: '20px', fontSize: '10px',
                      border: `1px solid ${s.haFila ? 'var(--ocean)' : 'var(--border-mid)'}`,
                      background: s.haFila ? 'rgba(31,147,224,0.15)' : 'transparent',
                      color: s.haFila ? 'var(--ocean)' : 'var(--text-muted)',
                      cursor: 'pointer', fontFamily: 'var(--font-sans)',
                    }}>
                      {s.haFila ? '↕ fila' : '= fisso'}
                    </button>
                    <button onClick={() => removeServizio(s.id)} style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      border: '1px solid var(--border)', background: 'transparent',
                      color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Preset */}
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Aggiungi preset
              </div>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {PRESET_SERVIZI.map(p => {
                  const già = localServizi.find(s => s.label === p.label);
                  return (
                    <button key={p.label} onClick={() => addPreset(p)} disabled={!!già}
                      style={{
                        padding: '5px 12px', borderRadius: '20px', fontSize: '12px',
                        border: '1px solid var(--border-mid)',
                        background: già ? 'var(--surface)' : 'transparent',
                        color: già ? 'var(--text-muted)' : 'var(--text-soft)',
                        cursor: già ? 'default' : 'pointer',
                        fontFamily: 'var(--font-sans)', opacity: già ? 0.5 : 1,
                      }}>
                      {p.icona} {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Personalizzato */}
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Personalizzato
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input placeholder="Nome" value={nuovoForm.label}
                  onChange={e => setNuovoForm(p => ({ ...p, label: e.target.value }))}
                  style={{ ...inputStyle, width: '130px', textAlign: 'left', padding: '6px 10px' }} />
                <input placeholder="🏖" value={nuovoForm.icona}
                  onChange={e => setNuovoForm(p => ({ ...p, icona: e.target.value }))}
                  style={{ ...inputStyle, width: '40px', textAlign: 'center', fontSize: '16px' }} />
                <div style={{ display: 'flex', gap: '4px' }}>
                  {COLORI.map(c => (
                    <div key={c} onClick={() => setNuovoForm(p => ({ ...p, colore: c }))}
                      style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: c,
                        cursor: 'pointer',
                        border: nuovoForm.colore === c ? '2px solid #fff' : '2px solid transparent',
                      }} />
                  ))}
                </div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={nuovoForm.haFila}
                    onChange={e => setNuovoForm(p => ({ ...p, haFila: e.target.checked }))} />
                  per fila
                </label>
                <button onClick={addCustomServizio}
                  style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-md)',
                    background: 'var(--surface)', border: '1px solid var(--border-mid)',
                    color: 'var(--text-soft)', fontSize: '12px', cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                  }}>
                  + Aggiungi
                </button>
              </div>
            </div>

            {/* Salva */}
            <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 'auto' }}>
              <button onClick={saveServiziHandler} disabled={savingServizi}
                style={{
                  padding: '8px 20px', borderRadius: 'var(--radius-md)',
                  background: 'var(--ocean)', border: 'none', color: '#fff',
                  fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                {savingServizi ? <Spinner size={14} /> : 'Salva servizi'}
              </button>
            </div>
          </div>
        )}

        {/* ── Area griglia ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', userSelect: 'none' }}>
          <div style={{
            height: '32px', background: 'rgba(31,147,224,0.12)', borderRadius: '8px 8px 0 0',
            marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', color: 'rgba(31,147,224,0.6)', letterSpacing: '0.06em',
          }}>〰 Mare 〰</div>

          {/* Header colonne */}
          <div style={{ display: 'grid', gridTemplateColumns: `28px repeat(${gridCols}, ${CELL_SIZE}px)`, gap: '2px', marginBottom: '2px' }}>
            <div />
            {Array.from({ length: gridCols }).map((_, ci) => (
              <div key={ci} onClick={() => fillCol(ci)} title={`Riempi colonna ${ci + 1}`}
                style={{
                  height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: '4px',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {ci + 1}
              </div>
            ))}
          </div>

          {/* Griglia */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `28px repeat(${gridCols}, ${CELL_SIZE}px)`,
            gridTemplateRows: `repeat(${gridRows}, ${CELL_SIZE}px)`,
            gap: '2px',
          }}>
            {Array.from({ length: gridRows }).map((_, rowIdx) => (
              <React.Fragment key={rowIdx}>
                <div onClick={() => fillRow(rowIdx)} title={`Riempi riga ${'ABCDEFGHIJKLMNOP'[rowIdx]}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', color: 'var(--text-muted)', background: 'var(--navy-mid)',
                    borderRadius: '4px', cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--navy-mid)'}>
                  {'ABCDEFGHIJKLMNOP'[rowIdx]}
                </div>
                {Array.from({ length: gridCols }).map((_, colIdx) => {
                  const cell = celle.find(c => c.col === colIdx && c.row === rowIdx);
                  const servizio = cell?.type !== 'vuoto' ? localServizi.find(s => s.id === cell?.type) : null;
                  return (
                    <GridCell key={`${colIdx}_${rowIdx}`} cell={cell} servizio={servizio}
                      size={CELL_SIZE} isFirstRow={rowIdx === 0}
                      onMouseDown={() => handleMouseDown(colIdx, rowIdx)}
                      onMouseEnter={() => handleMouseEnter(colIdx, rowIdx)} />
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          <div style={{ height: '20px', background: 'rgba(245,200,66,0.08)', borderRadius: '0 0 8px 8px', marginTop: '2px' }} />

          <div style={{ marginTop: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {localServizi.filter(s => s.haFila).map(s => (
              <div key={s.id} style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: s.colore }} />
                {s.label}: riga A = prima fila
              </div>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {localServizi.map(s => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)', padding: '4px 10px',
                  fontSize: '12px', color: 'var(--text-soft)',
                }}>
                  <span>{s.icona}</span>
                  <span style={{ color: 'var(--sand)', fontWeight: 500 }}>{counts[s.id] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal pubblica ── */}
      {showPublish && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: 'var(--navy-mid)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '28px', width: '420px',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '6px' }}>Pubblica il layout</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Seleziona il periodo in cui applicare questo layout.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[['Dal', publishDal, setPublishDal], ['Al', publishAl, setPublishAl]].map(([label, val, setter]) => (
                <div key={label}>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{label}</label>
                  <input type="date" value={val} onChange={e => setter(e.target.value)}
                    style={{
                      width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)',
                      borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px',
                      fontSize: '13px', fontFamily: 'var(--font-sans)',
                    }} />
                </div>
              ))}
            </div>
            {publishDal && publishAl && new Date(publishAl) >= new Date(publishDal) && (
              <div style={{
                background: 'rgba(31,147,224,0.1)', border: '1px solid rgba(31,147,224,0.2)',
                borderRadius: 'var(--radius-md)', padding: '10px 14px',
                fontSize: '12.5px', color: 'var(--ocean)', marginBottom: '16px',
              }}>
                Verranno create {Math.ceil((new Date(publishAl) - new Date(publishDal)) / 86400000) + 1} date di disponibilità
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="sand" onClick={handlePublish} disabled={publishing || !publishDal || !publishAl} fullWidth>
                {publishing ? <Spinner size={14} /> : 'Pubblica disponibilità'}
              </Button>
              <Button variant="ghost" onClick={() => setShowPublish(false)}>Annulla</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaletteItem({ label, icona, colore, active, count, haFila, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
      borderRadius: 'var(--radius-md)', cursor: 'pointer',
      background: active ? `${colore}20` : 'transparent',
      border: `1px solid ${active ? colore + '60' : 'transparent'}`,
      marginBottom: '4px', transition: 'all 0.15s',
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '6px',
        background: `${colore}25`, border: `1px solid ${colore}50`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', flexShrink: 0,
      }}>{icona}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', fontWeight: active ? 500 : 400, color: active ? '#fff' : 'var(--text-soft)' }}>
          {label}
        </div>
        {haFila && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>per fila</div>}
      </div>
      {count !== null && count > 0 && (
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>{count}</span>
      )}
    </div>
  );
}

function GridCell({ cell, servizio, size, isFirstRow, onMouseDown, onMouseEnter }) {
  const isEmpty = !cell || cell.type === 'vuoto';
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseDown={onMouseDown} onMouseEnter={onMouseEnter}
      onMouseOver={() => setHovered(true)} onMouseOut={() => setHovered(false)}
      style={{
        width: `${size}px`, height: `${size}px`, borderRadius: '6px',
        border: `1px solid ${isEmpty ? (hovered ? 'var(--border-mid)' : 'var(--border)') : (servizio?.colore + '60' || 'var(--border)')}`,
        background: isEmpty ? (hovered ? 'var(--surface-hover)' : 'var(--surface)') : (servizio?.colore + '25' || 'var(--surface)'),
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        cursor: 'crosshair', transition: 'border-color 0.08s, background 0.08s', position: 'relative',
      }}>
      {!isEmpty && (
        <>
          <span style={{ fontSize: size > 40 ? '16px' : '12px', lineHeight: 1 }}>{servizio?.icona || '?'}</span>
          {size > 36 && (
            <span style={{ fontSize: '9px', color: servizio?.colore || 'var(--text-muted)', marginTop: '2px', fontWeight: 500 }}>
              {cell.id}
            </span>
          )}
          {isFirstRow && servizio?.haFila && (
            <div style={{ position: 'absolute', top: '2px', right: '2px', width: '5px', height: '5px', borderRadius: '50%', background: 'var(--sand)' }} />
          )}
        </>
      )}
    </div>
  );
}

function ToolbarItem({ label, icona, colore, active, count, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '5px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
      background: active ? `${colore}25` : 'transparent',
      border: `1px solid ${active ? colore + '70' : 'transparent'}`,
      transition: 'all 0.15s',
    }}>
      <span style={{ fontSize: '16px', lineHeight: 1 }}>{icona}</span>
      <span style={{ fontSize: '12.5px', color: active ? '#fff' : 'var(--text-soft)', fontWeight: active ? 500 : 400 }}>
        {label}
      </span>
      {count > 0 && (
        <span style={{
          fontSize: '10px', color: colore, fontWeight: 500,
          background: colore + '20', padding: '1px 6px', borderRadius: '10px',
        }}>
          {count}
        </span>
      )}
    </div>
  );
}